import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userAPI, UserUsage } from '../../services/user';
import { useTranslation } from 'react-i18next';

export default function UsageIndicator() {
  const { isAuthenticated } = useAuth();
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    const fetchUsage = async () => {
      try {
        const data = await userAPI.getUsage();
        setUsage(data);
      } catch {
        // Ignore errors - indicator is optional
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsage();

    // Refresh every 5 minutes
    const interval = setInterval(fetchUsage, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  if (!isAuthenticated || isLoading || !usage) {
    return null;
  }

  const remainingAnalyses = Math.max(0, usage.analysesLimit - usage.analysesCount);
  const isLow = remainingAnalyses <= 1 && usage.creditsBalance === 0;
  const isExhausted = remainingAnalyses === 0 && usage.creditsBalance === 0;

  return (
    <Link
      to="/usage"
      className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
        isExhausted
          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
          : isLow
          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
          : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
      }`}
      title={t('usageIndicator.remainingAnalyses')}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
      <span className="font-medium">
        {remainingAnalyses}
        {usage.creditsBalance > 0 && (
          <span className="text-blue-600 dark:text-blue-400 ml-1">+{usage.creditsBalance}</span>
        )}
      </span>
    </Link>
  );
}
